/**
 * Margens (drill-down de "margem estimada").
 *
 * Mostra a margem por produto como o pricing-runtime calcula — inclusive os
 * estados honestos `missing_cost` (custo não sincronizado) e `no_offer`. Nunca
 * exibimos margem inventada para item sem custo.
 *
 * Migrado para `.tsx`/JSX real (Cluster A — Vendas, redesign por perfil; ver
 * `docs/architecture/ADR-0011-*.md`). O padrão visual (cartão tokenizado,
 * selo de margem com ícone lucide) já vinha do Redesign R6 — esta migração
 * troca só a sintaxe (`createElement` → JSX), sem mudar classe/ícone/texto.
 */
// Import explícito de `React` — `tsx --test` (motor de `npm test`) cai no
// transform JSX clássico mesmo com `jsx: "react-jsx"` no tsconfig; ver o
// mesmo comentário em `layout/AppShell.tsx`/`components/KpiCard.tsx`.
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Info, type LucideIcon, Percent, TriangleAlert } from "lucide-react";
import { StatTile } from "../ui/index.js";
import { RecordTable } from "../components/RecordTable.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { DrillDownHeader } from "../components/DrillDownHeader.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { marginRowsForKpi, parseKpiId } from "../dashboard/drilldown.js";
import { formatBrl, formatMarginStatus, formatPercent } from "../format.js";
import { AWAITING_CONTRACT_LABEL, isSourceAwaitingContract, type DashboardDataset } from "../dashboard/kpis.js";
import type { PricingMarginRow } from "../api/types.js";
import type { DrillDownViewProps } from "../dashboard/view-props.js";

/**
 * Selo de situação de margem: ícone + texto, nunca só cor (régua §2). `ok`
 * dentro do piso (success/check), `below_target` alerta (danger/triângulo),
 * `missing_cost`/`no_offer` estado honesto sem número (neutro/informação). O
 * texto vem de `formatMarginStatus` (preservado), a cor é só reforço.
 */
const MARGIN_STATUS_META: Record<string, { tone: string; Icon: LucideIcon }> = {
  ok: { tone: "border-success text-success", Icon: CheckCircle2 },
  below_target: { tone: "border-danger text-danger", Icon: TriangleAlert },
  missing_cost: { tone: "border-border-strong text-body", Icon: Info },
  no_offer: { tone: "border-border-strong text-body", Icon: Info }
};

function MarginStatusBadge({ status }: { status: string }): ReactElement {
  const meta = MARGIN_STATUS_META[status] ?? MARGIN_STATUS_META.missing_cost;
  const Icon = meta.Icon;
  return (
    <span
      className={"inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " + meta.tone}
      data-margin-status={status}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{formatMarginStatus(status)}</span>
    </span>
  );
}

export function MargensView({ dataset, kpiId, now }: DrillDownViewProps): ReactElement {
  // Fonte de pricing ausente no ambiente: declarar a ausência, não listar zero
  // item com piso de margem 0% (que leria como política aprovada de 0%).
  if (isSourceAwaitingContract(dataset, "pricing")) {
    return (
      <div className="page page--margens flex flex-col gap-6" data-page="margens">
        <DrillDownHeader title="Margem por produto" kpiId={kpiId} recordCount={0} recordNoun="item(ns)" />
        <StatusPanel
          kind="awaiting_source"
          title={`Resumo de margem ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota GET /api/pricing/summary faz parte do contrato e responde em desenvolvimento, mas ainda não é publicada no caminho de produção (Worker → Edge Functions). Sem a fonte não há margem, piso nem fila de aprovação a exibir."
        />
      </div>
    );
  }
  const rows = marginRowsForKpi(dataset, kpiId, now ?? new Date());
  return (
    <div className="page page--margens flex flex-col gap-6" data-page="margens">
      <DrillDownHeader title="Margem por produto" kpiId={kpiId} recordCount={rows.length} recordNoun="item(ns)" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-margins-summary="true">
        <StatTile label="Piso de margem aprovado" value={formatPercent(dataset.pricing.summary.minMarginPct)} icon={Percent} />
        <StatTile label="Itens sem custo sincronizado" value={dataset.pricing.summary.missingCost} icon={TriangleAlert} />
        <StatTile label="Recomendações aguardando aprovação" value={dataset.pricing.summary.pendingRecommendations} icon={Info} hint="Aprovação sempre humana" />
      </div>
      <p className="sr-only">
        {`Piso de margem aprovado: ${formatPercent(dataset.pricing.summary.minMarginPct)}. ` +
          `${dataset.pricing.summary.missingCost} item(ns) sem custo sincronizado e ` +
          `${dataset.pricing.summary.pendingRecommendations} recomendação(ões) de preço aguardando aprovação humana.`}
      </p>
      {/* Task 16: esta é a margem RECOMENDADA do Price Engine (projeção de
          preço). A margem REALIZADA (receita menos taxa/comissão/custo) por
          pedido e por produto vive na Rentabilidade — o mesmo piso aprovado,
          lados diferentes. */}
      <p className="page__note text-sm text-muted">
        <Link to="/financeiro/rentabilidade" className="page__note-link font-semibold text-brand-text hover:underline">
          Ver a margem realizada por pedido e por produto (Rentabilidade)
        </Link>
      </p>
      <RecordTable<PricingMarginRow>
        caption="Itens que compõem a margem estimada"
        rows={rows}
        idOf={(row) => row.productId}
        emptyMessage="Nenhum item com custo sincronizado: sem custo não há margem a exibir."
        // Fase 7 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
        animateRows
        columns={[
          { key: "product", label: "Produto", render: (row) => row.name },
          { key: "price", label: "Preço vigente", render: (row) => (row.priceCents === null ? "sem oferta" : formatBrl(row.priceCents)) },
          { key: "cost", label: "Custo", render: (row) => (row.costCents === null ? "não sincronizado" : formatBrl(row.costCents)) },
          { key: "margin", label: "Margem", render: (row) => (row.marginPct === null ? "—" : formatPercent(row.marginPct)) },
          { key: "status", label: "Situação", render: (row) => <MarginStatusBadge status={row.status} /> }
        ]}
      />
    </div>
  );
}

export function Margens(): ReactElement {
  const [searchParams] = useSearchParams();
  const kpiId = parseKpiId(searchParams.get("kpi"));
  return <DatasetBoundary>{(dataset: DashboardDataset) => <MargensView dataset={dataset} kpiId={kpiId} />}</DatasetBoundary>;
}
